import './ResultsPage.css'

import axios from 'axios'
import type { ForumModel } from 'database'
import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation, useParams } from 'react-router-dom'

import { useLogger } from '../logger'
import { useEventTracker } from '../providers/event-tracker-provider'
import { API_GATEWAY_URL } from '../utils/env'

interface Comment {
  content: string
  isAnonymous: boolean
}

interface ForumResults extends Omit<ForumModel, 'commentIds'> {
  comments: Comment[]
  selectedComment: Comment | null
  totalParticipants: number
  participantImageUrls?: string[]
  participantsCount?: number
  likeCount?: number
}

export default function ResultsPage() {
  const { forumName } = useParams<{ forumName: string }>()
  const location = useLocation()
  const [results, setResults] = useState<ForumResults | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState<'copy' | 'go'>('copy')
  const [isCopyAnimatingOut, setIsCopyAnimatingOut] = useState(false)
  const [isGoEntering, setIsGoEntering] = useState(false)
  const logger = useLogger()
  const { tracker: eventTracker, isReady } = useEventTracker()
  const fetchedRef = useRef(false)
  const lastFetchedForumRef = useRef<string | null>(null)

  // Check if we have forum data in the location state
  const forumFromState = location.state?.forum as ForumResults | undefined

  useEffect(() => {
    // If we already have forum data from navigation state, use it
    if (forumFromState) {
      logger.info('Using forum data from navigation state', forumFromState)

      // Always set results from state immediately - no need to show loading
      setResults(forumFromState)

      // If we have comments and a selected comment, skip API fetch
      // Otherwise continue to fetch data from API to enhance our state data
      if (forumFromState.comments?.length > 0 || forumFromState.selectedComment) {
        return
      }

      // Skip the loading indicator by always setting initial results
    }

    // Skip if we've already fetched this specific forum or if forumName is undefined
    if (!forumName || (fetchedRef.current && lastFetchedForumRef.current === forumName)) {
      return
    }

    const fetchResults = async () => {
      try {
        // Set fetched flag before the API call
        fetchedRef.current = true
        lastFetchedForumRef.current = forumName

        logger.info(`Fetching forum data for: ${forumName}`)
        const response = await axios.get(`${API_GATEWAY_URL}/forums/by-name/${forumName}`)

        // Extract forum data and ensure we have the correct ID field
        const forumData = {
          ...response.data.forum,
          forumId: response.data.forum.id || response.data.forum.forumId,
        }

        // If we already have some state data, merge it with the API response
        if (forumFromState) {
          setResults({
            ...forumFromState,
            comments: response.data.forum.comments || [],
            selectedComment: response.data.forum.selectedComment || null,
          })
        } else {
          setResults(forumData)
        }

        logger.info('Results data:', forumData)
      } catch (err) {
        setError('Failed to load results')
        logger.error('Failed to load results:', err)
        // Reset flags on error to allow retry
        fetchedRef.current = false
        lastFetchedForumRef.current = null
      }
    }

    fetchResults()

    // Cleanup function
    return () => {
      // Don't reset fetchedRef here, as we want to keep track of what we've fetched
      // across re-renders in development mode
    }
  }, [forumName, logger, forumFromState])

  // Track page viewed when data is available or on mount
  useEffect(() => {
    if (isReady && forumName) {
      eventTracker.track({ name: 'Link Results Page Viewed', forumName })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, forumName])

  useEffect(() => {
    if (!isGoEntering) {
      return
    }

    const timeout = setTimeout(() => setIsGoEntering(false), 300)
    return () => clearTimeout(timeout)
  }, [isGoEntering])

  // At this point either results or forumFromState is available
  // Use results if available, otherwise use forumFromState
  const displayData = results || forumFromState

  // Update the firstComment selection to prioritize selectedComment and fall back to finding a non-empty comment
  const firstComment =
    displayData?.selectedComment ||
    displayData?.comments?.find((comment: Comment) => comment.content && comment.content.trim() !== '')

  if (error) {
    return (
      <div className="results-container">
        <div className="results-content">
          <div className="results-card">
            <div className="error-text">{error}</div>
            <button onClick={() => (window.location.href = '/')} className="home-button">
              Back to Homepage
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Only show loading when no results AND no state data available
  if (!results && !forumFromState) {
    return (
      <div className="results-container">
        <div className="results-content">
          <div className="loading">Loading...</div>
        </div>
      </div>
    )
  }

  // Ensure displayData is not null before rendering
  if (!displayData) {
    return null
  }

  // Pre-prepare the comment data before rendering to avoid any staggered loading effects
  const commentToShow = firstComment

  const handleCopyCode = async () => {
    const code = displayData?.forumName || forumName || ''

    try {
      await navigator.clipboard.writeText(code)
    } catch (clipboardError) {
      logger.warn('Failed to copy invite code', clipboardError)
    }

    eventTracker.track({ name: 'Link Invite Code Copied', forumId: displayData?.forumId || '' })

    setIsCopyAnimatingOut(true)
    setTimeout(() => {
      setCurrentStep('go')
      setIsCopyAnimatingOut(false)
      setIsGoEntering(true)
    }, 250)
  }

  const handleGetMatched = () => {
    const code = displayData?.forumName || forumName || ''

    eventTracker.track({
      name: 'Link GetMatched Pressed',
      forumId: displayData?.forumId || '',
      forumName: code,
    })

    // Build deep-link string with session ID
    const deepLink = `nugle://?code=${encodeURIComponent(code)}`

    // App Store URL for Nugle app (works globally in any country)
    const appStoreUrl = 'https://apps.apple.com/app/nugle-be-heard/id6745102771'

    // Try to open the app first
    logger.info('Attempting to open deep link:', { deepLink })

    // Create a hidden iframe to attempt opening the app
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    document.body.appendChild(iframe)

    // Set a timeout to redirect to App Store if app doesn't open
    const timeout = setTimeout(() => {
      logger.info('App not installed, redirecting to App Store:', appStoreUrl)
      window.location.href = appStoreUrl
    }, 2000) // 2 second timeout

    // Try to open the app
    iframe.onload = () => {
      clearTimeout(timeout)
      document.body.removeChild(iframe)
    }

    iframe.src = deepLink

    // Alternative approach using window.location
    // This will work in most modern browsers
    window.location.href = deepLink
  }

  // Get participant avatars from backend data
  const participantAvatars = displayData?.participantImageUrls || []
  const displayAvatars = participantAvatars.slice(0, 3)
  const totalParticipants = displayData?.participantsCount || displayData?.totalParticipants || 0
  const additionalParticipants = Math.max(0, totalParticipants - displayAvatars.length)

  return (
    <div className="results-container results-page">
      <Helmet>
        <title>
          {displayData ? `"${displayData.statement}" - Discussion Results | Nugle` : 'Discussion Results | Nugle'}
        </title>
        <meta
          name="description"
          content={
            commentToShow
              ? `See what people think: "${commentToShow.content.substring(0, 150)}${
                  commentToShow.content.length > 150 ? '...' : ''
                }" - Join the discussion on Nugle`
              : 'See anonymous discussion results on Nugle'
          }
        />

        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@nugle_app" />
        <meta name="twitter:creator" content="@nugle_app" />
        <meta
          name="twitter:title"
          content={displayData ? `"${displayData.statement}"` : 'Anonymous Discussion Results'}
        />
        <meta
          name="twitter:description"
          content={
            commentToShow
              ? `"${commentToShow.content.substring(0, 200)}${
                  commentToShow.content.length > 200 ? '...' : ''
                }" - See what people think and join the discussion on Nugle`
              : 'See anonymous discussion results and join the conversation on Nugle'
          }
        />
        <meta name="twitter:image" content={`${window.location.origin}/nugle-circle.png`} />
        <meta name="twitter:image:alt" content="Nugle - Anonymous discussion platform for changing perspectives" />

        {/* Open Graph meta tags for broader social media support */}
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content={displayData ? `"${displayData.statement}"` : 'Anonymous Discussion Results'}
        />
        <meta
          property="og:description"
          content={
            commentToShow
              ? `"${commentToShow.content.substring(0, 200)}${
                  commentToShow.content.length > 200 ? '...' : ''
                }" - See what people think and join the discussion on Nugle`
              : 'See anonymous discussion results and join the conversation on Nugle'
          }
        />
        <meta property="og:image" content={`${window.location.origin}/nugle-circle.png`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="Nugle" />
      </Helmet>

      {/* Apply the fade-in animation to the entire content container */}
      <div className="results-content animate-fade-in">
        {/* Statement Card */}
        <div className="statement-card">
          <h1 className="statement">&quot;{displayData.statement}&quot;</h1>

          {/* Engagement Info */}
          <div className="engagement-info">
            <div className="likes-section">
              <svg
                className="heart-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  fill="#FF3B7E"
                  stroke="#FF3B7E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="likes-count">{displayData?.likeCount || 2}</span>
            </div>

            <div className="participants-section">
              <div className="avatar-stack">
                {displayAvatars.length > 0 ? (
                  displayAvatars.map((avatar, index) => (
                    <img key={index} src={avatar} alt="Participant" className="participant-avatar" />
                  ))
                ) : (
                  <>
                    <img src="/default_avatar.png" alt="Participant" className="participant-avatar" />
                    <img src="/default_avatar.png" alt="Participant" className="participant-avatar" />
                    <img src="/default_avatar.png" alt="Participant" className="participant-avatar" />
                  </>
                )}
              </div>
              {additionalParticipants > 0 && <span className="additional-count">+{additionalParticipants}</span>}
            </div>
          </div>

          {/* AI Powered Label */}
          <div className="ai-powered-label">
            <span className="lock-emoji">🔒</span>
            <span>ai-powered anonymous debates</span>
          </div>
        </div>

        <div className="interaction-wrapper">
          {currentStep === 'copy' && (
            <div className={`interaction-card copy-card ${isCopyAnimatingOut ? 'card-slide-out' : 'card-slide-in'}`}>
              <h2 className="section-title">Copy Invite Code</h2>
              <div className="code-container">
                <div className="code-display">{displayData?.forumName || forumName || 'Loading...'}</div>
                <button className="copy-button" onClick={handleCopyCode}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="#666" strokeWidth="2" fill="none" />
                    <path
                      d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                      stroke="#666"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {currentStep === 'go' && (
            <div className={`interaction-card go-card ${isGoEntering ? 'card-slide-in' : ''}`}>
              <h2 className="section-title">Ready to join?</h2>
              <button onClick={handleGetMatched} className="get-started-button">
                Go
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
